import React, { useEffect, useState } from "react";
import { Plus, Trash2, Briefcase, Users, Loader2, Pencil, LayoutGrid, List } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { toast } from "sonner";
import { fetchProducts, createProduct, updateProduct, deleteProduct, money } from "../../lib/api";

const categories = ["Credit", "Planning", "Wealth", "Insurance", "Funding", "Tax", "Business", "General"];
const empty = { name: "", description: "", price: 0, taxable: false, active: true, category: "General" };

export default function Products() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState(localStorage.getItem("rfl_services_view") || "grid");
  const [form, setForm] = useState(empty);

  const changeView = (v) => {
    setView(v);
    localStorage.setItem("rfl_services_view", v);
  };

  const load = async () => {
    try {
      setItems(await fetchProducts());
    } catch (e) {
      toast.error("Could not load services");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...p }); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Service name is required"); return; }
    const payload = { ...form, price: Number(form.price) || 0 };
    try {
      if (editing) await updateProduct(editing, payload);
      else await createProduct(payload);
      setOpen(false);
      await load();
      toast.success(editing ? "Service updated" : "Service added");
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const toggle = async (p, active) => {
    await updateProduct(p.id, { ...p, active });
    load();
  };

  const remove = async (id) => {
    await deleteProduct(id);
    load();
    toast.success("Service removed");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl font-bold text-navy">Services</h1>
          <p className="text-gray-500 text-sm mt-0.5">Invoiceable services &amp; offerings &middot; {items.length} total</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-white rounded-lg p-1 border border-gray-100">
            <button
              onClick={() => changeView("grid")}
              className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors ${view === "grid" ? "bg-navy text-white" : "text-gray-500 hover:bg-gray-100"}`}
              data-testid="services-view-grid"
              aria-label="Grid view"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => changeView("list")}
              className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors ${view === "list" ? "bg-navy text-white" : "text-gray-500 hover:bg-gray-100"}`}
              data-testid="services-view-list"
              aria-label="List view"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          <Button onClick={openNew} className="bg-navy hover:bg-navy-light text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add Service
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
      ) : view === "list" ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="services-list-view">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-400">
                  <th className="px-4 py-3 font-medium">Service</th>
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium text-right">Price</th>
                  <th className="px-4 py-3 font-medium text-center">Active</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className={`border-b border-gray-50 last:border-0 hover:bg-cream/40 ${s.active ? "" : "opacity-60"}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-navy">{s.name}</div>
                      {s.description && <div className="text-xs text-gray-400 line-clamp-1 max-w-md">{s.description}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-block bg-navy/5 text-navy text-xs px-2.5 py-1 rounded-md">{s.category}</span>
                      {s.taxable && <span className="ml-1 inline-block bg-amber-50 text-amber-700 text-xs px-2.5 py-1 rounded-md">Taxable</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-serif font-bold text-navy">{money(s.price)}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center">
                        <Switch checked={s.active} onCheckedChange={(v) => toggle(s, v)} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => openEdit(s)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-navy/5 hover:text-navy transition-colors">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => remove(s.id)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-gray-400">No services yet. Add your first invoiceable service.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => (
            <div key={s.id} className={`bg-white rounded-xl p-5 border shadow-sm transition-all ${s.active ? "border-gray-100" : "border-gray-100 opacity-60"}`}>
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-lg bg-cream flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-gold-dark" />
                </div>
                <Switch checked={s.active} onCheckedChange={(v) => toggle(s, v)} />
              </div>
              <h3 className="mt-4 font-serif text-lg font-semibold text-navy leading-snug">{s.name}</h3>
              {s.description && <p className="mt-1 text-sm text-gray-500 line-clamp-2">{s.description}</p>}
              <div className="mt-2 flex items-center gap-2">
                <span className="inline-block bg-navy/5 text-navy text-xs px-2.5 py-1 rounded-md">{s.category}</span>
                {s.taxable && <span className="inline-block bg-amber-50 text-amber-700 text-xs px-2.5 py-1 rounded-md">Taxable</span>}
              </div>              <div className="mt-4 flex items-center justify-between">
                <span className="font-serif text-xl font-bold text-navy">{money(s.price)}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(s)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-navy/5 hover:text-navy transition-colors">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(s.id)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="col-span-full bg-white rounded-xl p-12 text-center text-gray-400 border border-dashed border-gray-200">
              No services yet. Add your first invoiceable service.
            </div>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-navy">{editing ? "Edit Service" : "Add Service"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Input placeholder="Service name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" step="0.01" placeholder="Price ($)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <Switch checked={form.taxable} onCheckedChange={(v) => setForm({ ...form, taxable: v })} /> Taxable item
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-navy hover:bg-navy-light text-white">{editing ? "Save Changes" : "Add Service"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
